import React, { useEffect, useMemo, useState } from 'react'
import { Formik, Form, Field } from 'formik'
import * as Yup from 'yup'
import { CustomContentModal } from '../../../../components/CustomContentModal'
import Button from '../../../../components/Form/Button'
import Input from '../../../../components/Form/Input'
import Select from '../../../../components/Form/Select'
import { getInputStyles } from '../../../../components/Form/styles'
import {
  createMeterServiceApi,
  getTenantAccountTariffOptionsApi,
  MeterServiceCreatePayload,
  MeterServiceTariffOptions,
} from '../../../../apis/apis'
import { ServiceAgreement } from '../../../../apis/types'
import { pushToast } from '../../../../components/Toaster/Toaster.slice'
import { useAppDispatch } from '../../../../shared/redux/hooks'
import { groupCcaTariffsByUtility, ccaTariffOptionKey, ccaTariffOptionLabel } from './serviceAgreementTariffUtils'

export interface ServiceAgreementCreateModalProps {
  isOpen: boolean
  tenantAccountId: string
  existingAgreements: ServiceAgreement[]
  ccaProgram?: string
  onClose: () => void
  onSaved: () => void | Promise<void>
}

type FormValues = {
  serviceAgreementId: string
  meterNumber: string
  startDate: string
  endDate: string
  tariffSchedule: string
  tariffEffectiveDate: string
  tariffEndDate: string
  ccaTariffSchedule: string
  ccaTariffUtilityShortName: string
  ccaTariffEffectiveDate: string
  ccaTariffEndDate: string
  ccaVintageYear: string
  meterAccountType: 'BENEFITTING' | 'GENERATOR'
  postBillingTransition: boolean
  street1: string
  street2: string
  city: string
  state: string
  zip: string
}

const todayIso = (): string => {
  const d = new Date()
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${d.getFullYear()}-${month}-${day}`
}

const ServiceAgreementCreateModal: React.FC<ServiceAgreementCreateModalProps> = ({
  isOpen,
  tenantAccountId,
  existingAgreements,
  ccaProgram,
  onClose,
  onSaved,
}) => {
  const dispatch = useAppDispatch()
  const [step, setStep] = useState<'form' | 'review'>('form')
  const [draft, setDraft] = useState<FormValues | null>(null)
  const [options, setOptions] = useState<MeterServiceTariffOptions | null>(null)
  const [loadingOptions, setLoadingOptions] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const meterNumbers = useMemo(() => {
    const set = new Set<string>()
    for (const sa of existingAgreements) {
      if (sa.meter?.meterNumber) set.add(sa.meter.meterNumber)
    }
    return [...set].sort()
  }, [existingAgreements])

  const siblingAddress = existingAgreements[0]?.serviceAddress

  const initialValues: FormValues = useMemo(
    () => ({
      serviceAgreementId: '',
      meterNumber: meterNumbers[0] ?? '',
      startDate: todayIso(),
      endDate: '',
      tariffSchedule: '',
      tariffEffectiveDate: '',
      tariffEndDate: '',
      ccaTariffSchedule: '',
      ccaTariffUtilityShortName: '',
      ccaTariffEffectiveDate: '',
      ccaTariffEndDate: '',
      ccaVintageYear: '',
      meterAccountType: 'BENEFITTING',
      postBillingTransition: false,
      street1: siblingAddress?.street1 ?? '',
      street2: siblingAddress?.street2 ?? '',
      city: siblingAddress?.city ?? '',
      state: siblingAddress?.state ?? '',
      zip: siblingAddress?.zip ?? '',
    }),
    [meterNumbers, siblingAddress]
  )

  useEffect(() => {
    if (!isOpen) {
      setStep('form')
      setDraft(null)
      setOptions(null)
      return
    }

    let cancelled = false
    setLoadingOptions(true)
    void getTenantAccountTariffOptionsApi(tenantAccountId)
      .then((opts) => {
        if (!cancelled) setOptions(opts)
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setOptions(null)
          void dispatch(
            pushToast({
              type: 'error',
              message: 'Could not load tariff options',
              description: error instanceof Error ? error.message : undefined,
            })
          )
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingOptions(false)
      })

    return () => {
      cancelled = true
    }
  }, [isOpen, tenantAccountId, dispatch])

  if (!isOpen) return null

  const ccaApplies =
    options?.ccaApplies === true ||
    ccaProgram === 'ENROLLED' ||
    (options?.ccaTariffs?.length ?? 0) > 0
  const valuesForRank = draft?.tariffSchedule || initialValues.tariffSchedule
  const ccaGroups = groupCcaTariffsByUtility(
    options?.ccaTariffs ?? [],
    options?.ccaUtilityShortName ?? null,
    valuesForRank
  )

  const serviceTariffOptions: Record<string, string> = Object.fromEntries(
    (options?.serviceTariffs ?? []).map((t) => [
      t.schedule,
      t.name ? `${t.schedule} — ${t.name}` : t.schedule,
    ])
  )

  const review = draft

  const handleConfirm = async () => {
    if (draft == null) return
    setIsSubmitting(true)
    try {
      const payload: MeterServiceCreatePayload = {
        serviceAgreementId: draft.serviceAgreementId.trim(),
        meterNumber: draft.meterNumber.trim(),
        startDate: draft.startDate,
        tariffSchedule: draft.tariffSchedule,
        meterAccountType: draft.meterAccountType,
        postBillingTransition: draft.postBillingTransition,
      }
      if (draft.endDate) payload.endDate = draft.endDate
      if (draft.tariffEffectiveDate) payload.tariffEffectiveDate = draft.tariffEffectiveDate
      if (draft.tariffEndDate) payload.tariffEndDate = draft.tariffEndDate
      if (draft.street1 && draft.city && draft.state && draft.zip) {
        payload.serviceAddress = {
          street1: draft.street1,
          street2: draft.street2 || undefined,
          city: draft.city,
          state: draft.state,
          zip: draft.zip,
        }
      }
      if (ccaApplies && draft.ccaTariffSchedule) {
        payload.ccaTariffSchedule = draft.ccaTariffSchedule
        payload.ccaTariffUtilityShortName = draft.ccaTariffUtilityShortName
        if (draft.ccaTariffEffectiveDate) payload.ccaTariffEffectiveDate = draft.ccaTariffEffectiveDate
        if (draft.ccaTariffEndDate) payload.ccaTariffEndDate = draft.ccaTariffEndDate
        if (draft.ccaVintageYear) payload.ccaVintageYear = draft.ccaVintageYear
      }

      await createMeterServiceApi(tenantAccountId, payload)
      await onSaved()
      void dispatch(pushToast({ type: 'success', message: 'Service agreement created' }))
      onClose()
    } catch (error) {
      void dispatch(
        pushToast({
          type: 'error',
          message: 'Could not create service agreement',
          description: error instanceof Error ? error.message : undefined,
        })
      )
      setStep('form')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <CustomContentModal isOpen={isOpen} onClose={isSubmitting ? () => undefined : onClose}>
      <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-lg bg-white p-6 text-left shadow-xl dark:bg-slate-800">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white">
          {step === 'form' ? 'Add service agreement' : 'Confirm new service agreement'}
        </h3>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Superuser only. Creates a new meter service on this account (not an OCR history chain).
        </p>

        {step === 'form' ? (
          <Formik
            enableReinitialize
            initialValues={initialValues}
            validationSchema={Yup.object({
              serviceAgreementId: Yup.string().required('Required'),
              meterNumber: Yup.string().required('Required'),
              startDate: Yup.string().required('Required'),
              tariffSchedule: Yup.string().required('Required'),
              meterAccountType: Yup.string().oneOf(['BENEFITTING', 'GENERATOR']).required(),
            })}
            onSubmit={(values) => {
              setDraft(values)
              setStep('review')
            }}
          >
            {({ values, setFieldValue, isSubmitting: formSubmitting }) => (
              <Form className="mt-4 space-y-3" aria-busy={loadingOptions}>
                <Field as={Input} name="serviceAgreementId" label="Service agreement ID" required />
                <div>
                  <Field as={Input} name="meterNumber" label="Meter number" required list="existing-meters" />
                  <datalist id="existing-meters">
                    {meterNumbers.map((m) => (
                      <option key={m} value={m} />
                    ))}
                  </datalist>
                  <p className="mt-0.5 text-xs text-slate-500">
                    Pick an existing meter on this account or enter a new number.
                  </p>
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <Field as={Input} type="date" name="startDate" label="Start date" required />
                  <Field as={Input} type="date" name="endDate" label="End date" />
                </div>
                <Field
                  component={Select}
                  name="tariffSchedule"
                  label="Service tariff"
                  required
                  disabled={loadingOptions}
                  options={
                    loadingOptions && Object.keys(serviceTariffOptions).length === 0
                      ? { '': 'Loading tariffs…' }
                      : serviceTariffOptions
                  }
                />
                <div className="grid gap-3 sm:grid-cols-2">
                  <Field as={Input} type="date" name="tariffEffectiveDate" label="Tariff effective start" />
                  <Field as={Input} type="date" name="tariffEndDate" label="Tariff effective end" />
                </div>

                {ccaApplies ? (
                  <div className="space-y-3 rounded-md border border-slate-200 p-3 dark:border-slate-600">
                    <p className="text-xs font-medium uppercase tracking-wide text-slate-500">CCA</p>
                    <div>
                      <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-200">
                        CCA tariff
                      </label>
                      <select
                        className={getInputStyles({ disabled: loadingOptions })}
                        disabled={loadingOptions}
                        value={
                          values.ccaTariffSchedule
                            ? values.ccaTariffUtilityShortName
                              ? `${values.ccaTariffSchedule}||${values.ccaTariffUtilityShortName}`
                              : values.ccaTariffSchedule
                            : ''
                        }
                        onChange={(e) => {
                          const raw = e.target.value
                          if (!raw) {
                            void setFieldValue('ccaTariffSchedule', '')
                            void setFieldValue('ccaTariffUtilityShortName', '')
                            return
                          }
                          const [schedule, utility] = raw.split('||')
                          void setFieldValue('ccaTariffSchedule', schedule)
                          void setFieldValue('ccaTariffUtilityShortName', utility ?? '')
                        }}
                      >
                        <option value="">Not set</option>
                        {ccaGroups.map((group) => (
                          <optgroup key={group.label} label={group.label}>
                            {group.tariffs.map((t) => {
                              const key = ccaTariffOptionKey(t)
                              return (
                                <option key={key || 'none'} value={key}>
                                  {ccaTariffOptionLabel(t)}
                                </option>
                              )
                            })}
                          </optgroup>
                        ))}
                      </select>
                    </div>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <Field
                        as={Input}
                        type="date"
                        name="ccaTariffEffectiveDate"
                        label="CCA effective start"
                      />
                      <Field as={Input} type="date" name="ccaTariffEndDate" label="CCA effective end" />
                    </div>
                    <Field
                      as={Input}
                      name="ccaVintageYear"
                      label="CCA vintage year"
                      description="Used for generation rate lookup"
                    />
                  </div>
                ) : null}

                <Field
                  component={Select}
                  name="meterAccountType"
                  label="Meter account type"
                  required
                  options={{ BENEFITTING: 'Benefitting', GENERATOR: 'Generator' }}
                />
                <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
                  <input
                    type="checkbox"
                    className="h-4 w-4"
                    checked={values.postBillingTransition}
                    onChange={(e) => void setFieldValue('postBillingTransition', e.target.checked)}
                  />
                  Post-billing transition
                </label>

                <div className="space-y-2 rounded-md border border-slate-200 p-3 dark:border-slate-600">
                  <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                    Service address (optional if account already has an SA)
                  </p>
                  <Field as={Input} name="street1" label="Street" />
                  <Field as={Input} name="street2" label="Street 2" />
                  <div className="grid gap-3 sm:grid-cols-3">
                    <Field as={Input} name="city" label="City" />
                    <Field as={Input} name="state" label="State" />
                    <Field as={Input} name="zip" label="ZIP" />
                  </div>
                </div>

                <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                  <Button type="button" color="slate" disabled={formSubmitting} onClick={onClose}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={formSubmitting || loadingOptions}>
                    Review
                  </Button>
                </div>
              </Form>
            )}
          </Formik>
        ) : review != null ? (
          <div className="mt-4 space-y-3">
            <ul className="space-y-1 text-sm text-slate-700 dark:text-slate-200">
              <li>
                <span className="font-medium">SA ID:</span> {review.serviceAgreementId}
              </li>
              <li>
                <span className="font-medium">Meter:</span> {review.meterNumber}
              </li>
              <li>
                <span className="font-medium">Start:</span> {review.startDate}
                {review.endDate ? ` → ${review.endDate}` : ''}
              </li>
              <li>
                <span className="font-medium">Tariff:</span> {review.tariffSchedule}
              </li>
              {review.ccaTariffSchedule ? (
                <li>
                  <span className="font-medium">CCA:</span>{' '}
                  {review.ccaTariffUtilityShortName
                    ? `${review.ccaTariffUtilityShortName} · ${review.ccaTariffSchedule}`
                    : review.ccaTariffSchedule}
                  {review.ccaVintageYear ? ` (vintage ${review.ccaVintageYear})` : ''}
                </li>
              ) : null}
              <li>
                <span className="font-medium">Type:</span>{' '}
                {review.meterAccountType === 'GENERATOR' ? 'Generator' : 'Benefitting'}
              </li>
              <li>
                <span className="font-medium">Post-billing transition:</span>{' '}
                {review.postBillingTransition ? 'Yes' : 'No'}
              </li>
            </ul>
            <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <Button type="button" color="slate" disabled={isSubmitting} onClick={() => setStep('form')}>
                Back
              </Button>
              <Button type="button" disabled={isSubmitting} onClick={() => void handleConfirm()}>
                {isSubmitting ? 'Creating…' : 'Confirm'}
              </Button>
            </div>
          </div>
        ) : null}
      </div>
    </CustomContentModal>
  )
}

export default ServiceAgreementCreateModal
